import { useEffect, useMemo, useState } from 'react';
import { Modal, Button } from 'antd';
import { ITableFilters } from '@interfaces';

import { CustomerStatusEnumerator, LeadStatusEnum, SegmentEnumerator } from '@rasayi-workspace/shared';
import { useLeadContext } from '@contexts';
import SelectInput from '../select';
import { filterBarkeys } from '../types';
import { City, State } from 'country-state-city';
import QuantitySelect from '../QuantityInput.tsx';
import DateSelect from '../dateSelect';

interface IFilterModal {
    visible: boolean;
    onClose: () => void;
    setvalues: (values: ITableFilters) => void;
    values: ITableFilters;
    filtersToShow: filterBarkeys[];
}

const FilterModal = ({ setvalues, values, visible, onClose, filtersToShow }: IFilterModal) => {
    const [filters, setFilters] = useState<ITableFilters>(values);
    const { agents, productType, channels, brands } = useLeadContext();

    useEffect(() => {
        setFilters(values);
    }, [values]);

    const handleApply = () => {
        setvalues(filters);
        onClose();
    };
    const clearFilters = () => {
        setFilters({
            agent: [],
            status: [],
            city: [],
            segment: [],
            productType: [],
            channel: [],
            quantity: null,
            brand: [],
            createdAt: null,
            searchedText: '',
            province: [],
            customerStatus: [],
        });
    };
    const clearvalues = () => {
        setvalues({
            agent: [],
            status: [],
            city: [],
            segment: [],
            productType: [],
            channel: [],
            quantity: null,
            brand: [],
            createdAt: null,
            searchedText: '',
            province: [],
            customerStatus: [],
        });
    };

    // Function to handle Clear
    const handleClear = () => {
        clearFilters();
        clearvalues();
    };

    const onCancelHandler = () => {
        onClose();
        clearFilters();
    };

    const provinceCities = useMemo(() => {
        return (
            City.getCitiesOfCountry('PK')?.map(state => {
                return {
                    value: state.name,
                    label: state.name,
                };
            }) || []
        );
    }, [City]);

    const pakStates = useMemo(
        () =>
            State.getStatesOfCountry('PK').map(state => {
                return {
                    code: state.isoCode,
                    value: state.name,
                    label: state.name,
                };
            }),
        [State]
    );

    const isVisible = (key: filterBarkeys) => filtersToShow.includes(key);

    return (
        <Modal title="Filters" open={visible} onCancel={onCancelHandler} footer={null} width={650} className="custom-modal">
            <div className="grid grid-cols-2 gap-x-6 gap-y-2">
                {isVisible('agnet') ? (
                    <SelectInput
                        label="Agent"
                        options={agents?.map(obj => ({ label: obj.name, value: obj.id }))}
                        placeholder="Search Agent"
                        clasNames="!text-[#007AFF] !bg-white text-[12px]"
                        isLabel={true}
                        value={filters.agent}
                        onChange={val => setFilters(prev => ({ ...prev, agent: val }))}
                        mode="multiple"
                    />
                ) : null}
                {isVisible('lead_status') ? (
                    <SelectInput
                        label="Status"
                        options={Object.values(LeadStatusEnum).map(status => ({ label: status, value: status }))}
                        placeholder="Search Status"
                        clasNames="!text-[#007AFF] !bg-white text-[12px]"
                        isLabel={true}
                        value={filters.status}
                        onChange={val => setFilters(prev => ({ ...prev, status: val }))}
                        mode="multiple"
                    />
                ) : null}

                {isVisible('customer_status') ? (
                    <SelectInput
                        label="Status"
                        options={Object.values(CustomerStatusEnumerator).map(status => ({ label: status, value: status }))}
                        placeholder="Search Status"
                        clasNames="!text-[#007AFF] !bg-white text-[12px]"
                        isLabel={true}
                        value={filters.customerStatus}
                        onChange={val => setFilters(prev => ({ ...prev, customerStatus: val }))}
                        mode="multiple"
                    />
                ) : null}

                {isVisible('province') ? (
                    <SelectInput
                        label="Province"
                        options={pakStates}
                        placeholder="Search province"
                        clasNames="!text-[#007AFF] !bg-white text-[12px]"
                        isLabel={true}
                        value={filters.province}
                        onChange={val => setFilters(prev => ({ ...prev, province: val }))}
                        mode="multiple"
                    />
                ) : null}

                {isVisible('city') ? (
                    <SelectInput
                        label="City"
                        options={provinceCities}
                        placeholder="Search city"
                        clasNames="!text-[#007AFF] !bg-white text-[12px]"
                        isLabel={true}
                        value={filters.city}
                        onChange={val => setFilters(prev => ({ ...prev, city: val }))}
                        mode="multiple"
                    />
                ) : null}

                {isVisible('segment') ? (
                    <SelectInput
                        label="Segment"
                        options={Object.values(SegmentEnumerator).map(segment => ({
                            label: segment,
                            value: segment,
                        }))} // Mapping SegmentEnumerator to options
                        placeholder="Search Segment"
                        clasNames="!text-[#007AFF] !bg-white text-[12px]"
                        isLabel={true}
                        value={filters.segment}
                        onChange={val => setFilters(prev => ({ ...prev, segment: val }))}
                        mode="multiple"
                    />
                ) : null}

                {isVisible('channel') ? (
                    <SelectInput
                        label="Channel"
                        options={channels.map(channel => ({
                            label: channel.name,
                            value: channel.id,
                        }))}
                        placeholder="Search Channel"
                        clasNames="!text-[#007AFF] !bg-white text-[12px]"
                        isLabel={true}
                        value={filters.channel}
                        onChange={val => setFilters(prev => ({ ...prev, channel: val }))}
                        mode="multiple"
                    />
                ) : null}

                {isVisible('segment') ? (
                    <SelectInput
                        label="Product Type"
                        options={productType?.map(obj => ({ label: obj.name, value: obj.id }))} // Assuming productTypes is available
                        placeholder="Search Product Type"
                        clasNames="!text-[#007AFF] !bg-white text-[12px]"
                        isLabel={true}
                        value={filters.productType}
                        onChange={val => setFilters(prev => ({ ...prev, productType: val }))}
                        mode="multiple"
                    />
                ) : null}

                {isVisible('quantity') ? <QuantitySelect value={filters.quantity} onChange={val => setFilters(prev => ({ ...prev, quantity: val }))} /> : null}

                {isVisible('brand') ? (
                    <SelectInput
                        label="Brand"
                        options={brands.map(brand => ({
                            label: brand.name,
                            value: brand.id,
                        }))}
                        placeholder="Search Brand"
                        clasNames="!text-[#007AFF] !bg-white text-[12px]"
                        isLabel={true}
                        value={filters.brand}
                        onChange={val => setFilters(prev => ({ ...prev, brand: val }))}
                        mode="multiple"
                    />
                ) : null}

                {isVisible('created_at') ? (
                    <div className="w-full">
                        <DateSelect placeholder="Select date" label="Date" value={filters.createdAt} onChange={val => setFilters(prev => ({ ...prev, createdAt: val }))} />
                    </div>
                ) : null}
            </div>

            {/* Buttons */}
            <div className="flex justify-end gap-4 mt-4 bg-white">
                <Button className="bg-white text-[#4A4747] px-5 rounded-lg w-[115px] h-[40px] hover:!text-black hover:!border-[#DBD7D7]" onClick={handleClear}>
                    Clear
                </Button>
                <Button className="bg-[#BD273B] text-white hover:!text-white hover:!border-[#BD273B] px-5 rounded-lg w-[115px] h-[40px]" onClick={handleApply}>
                    Add filter
                </Button>
            </div>
        </Modal>
    );
};

export default FilterModal;
